import React, { useCallback, useEffect, useState } from "react";

import { FormValues } from "components/Deal-amortization/schemas";
import { DebtSegment } from "store/types";

export interface DealTab {
  dealId: number;
  dealTitle: string;
  segments: DebtSegment[];
  isFacility: boolean;
  isNew: boolean;
  isDirty: boolean;
}

interface DefaultValues extends Partial<DebtSegment> {
  deal_id: number;
  deal_title: string;
}

interface UseFacilityTabsProps {
  open: boolean;
  facilitySegments: DebtSegment[];
  withdrawalSegments: DebtSegment[];
  dealId: number;
  dealTitle: string;
  isExistingFacility: boolean;
  openWithdrawalTab?: boolean;
  openNewChangeTab?: { dealId: number; date: string };
  defaultValues: DefaultValues;
}

export function useFacilityTabs({
  open,
  facilitySegments,
  withdrawalSegments,
  dealId,
  dealTitle,
  isExistingFacility,
  openWithdrawalTab = false,
  openNewChangeTab = undefined,
  defaultValues
}: UseFacilityTabsProps) {
  const [primaryTabIndex, setPrimaryTabIndex] = useState(0);
  const [secondaryTabIndex, setSecondaryTabIndex] = useState(0);
  const [dealTabs, setDealTabs] = useState<DealTab[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingTabSwitch, setPendingTabSwitch] = useState<number | null>(null);
  const [pendingAddWithdrawal, setPendingAddWithdrawal] = useState(false);

  // Initialize deal tabs from facilitySegments and withdrawalSegments
  useEffect(() => {
    if (!open) return;

    const tabs: DealTab[] = [];
    let primaryTabIndex = 0;
    let secondaryTabIndex = -1;

    // Add facility tab if it exists
    if (facilitySegments.length > 0) {
      const sortedFacilitySegments = [...facilitySegments].sort((a, b) => a.start_date.localeCompare(b.start_date));
      tabs.push({
        dealId: sortedFacilitySegments[0].deal_id,
        dealTitle: sortedFacilitySegments[0].deal_title || "Facility",
        segments: sortedFacilitySegments,
        isFacility: true,
        isNew: false,
        isDirty: false
      });
    }

    // Group withdrawal segments by deal_id
    const withdrawalsByDeal = withdrawalSegments.reduce(
      (acc, segment) => {
        if (!acc[segment.deal_id]) {
          acc[segment.deal_id] = [];
        }
        acc[segment.deal_id].push(segment);
        return acc;
      },
      {} as Record<number, DebtSegment[]>
    );

    // Add withdrawal tabs
    Object.entries(withdrawalsByDeal).forEach(([dealIdStr, segments]) => {
      const sortedSegments = [...segments].sort((a, b) => a.start_date.localeCompare(b.start_date));
      tabs.push({
        dealId: parseInt(dealIdStr),
        dealTitle: sortedSegments[0].deal_title || `Withdrawal ${tabs.length}`,
        segments: sortedSegments,
        isFacility: false,
        isNew: false,
        isDirty: false
      });
    });

    // Handle openWithdrawalTab - add a new empty withdrawal tab
    if (openWithdrawalTab && isExistingFacility) {
      tabs.push({
        dealId: 0,
        dealTitle: `Withdrawal ${tabs.length}`,
        segments: [],
        isFacility: false,
        isNew: true,
        isDirty: true
      });
      primaryTabIndex = tabs.length - 1;
    }

    // Handle openNewChangeTab - add a new segment to an existing deal at the specified date
    if (openNewChangeTab && isExistingFacility) {
      const { dealId, date } = openNewChangeTab;
      let targetTabIndex = -1;
      let newSegmentIndex = -1;

      // Find the tab with the matching dealId
      for (let i = 0; i < tabs.length; i++) {
        if (tabs[i].dealId === dealId) {
          targetTabIndex = i;
          const tabSegments = tabs[i].segments;

          // First, check if a segment with this exact date already exists
          const existingSegmentIndex = tabSegments.findIndex((seg) => seg.start_date === date);
          if (existingSegmentIndex !== -1) {
            // Segment already exists, just select it
            newSegmentIndex = existingSegmentIndex;
            break;
          }

          // Find the position where this new segment should be inserted
          for (let j = 0; j < tabSegments.length; j++) {
            const currentSegment = tabSegments[j];
            const nextSegment = tabSegments[j + 1];

            // Check if the date is within this segment's range or after the last segment
            if (date > currentSegment.start_date && (!nextSegment || date < nextSegment.start_date)) {
              // Create a new segment based on the current one
              const newSegment = {
                ...currentSegment,
                id: 0,
                start_date: date
              };
              // Insert the new segment after the current one
              tabSegments.splice(j + 1, 0, newSegment);
              newSegmentIndex = j + 1;
              tabs[i].isDirty = true;
              break;
            }
          }

          break;
        }
      }

      if (targetTabIndex !== -1 && newSegmentIndex !== -1) {
        primaryTabIndex = targetTabIndex;
        secondaryTabIndex = newSegmentIndex;
      }
    }

    // New facility case
    if (!tabs.length) {
      tabs.push({
        dealId: dealId,
        dealTitle: dealTitle || "Facility",
        segments: [
          {
            ...defaultValues,
            deal_id: dealId,
            deal_title: dealTitle || "Facility"
          } as DebtSegment
        ],
        isFacility: true,
        isNew: true,
        isDirty: false
      });
    }

    setDealTabs(tabs);
    setPrimaryTabIndex(primaryTabIndex);
    secondaryTabIndex =
      secondaryTabIndex !== -1 ? secondaryTabIndex : Math.max(0, tabs[primaryTabIndex].segments.length - 1);
    setSecondaryTabIndex(secondaryTabIndex);
  }, [open]);

  const currentPrimaryTab = dealTabs[primaryTabIndex];
  const currentSegments = currentPrimaryTab?.segments || [];
  const currentSegment = currentSegments[secondaryTabIndex];
  const isFacilityTab = currentPrimaryTab?.isFacility || false;

  const handleAddWithdrawal = useCallback(() => {
    if (currentPrimaryTab?.isDirty) {
      setPendingAddWithdrawal(true);
      setShowWarning(true);
    } else {
      const newTab: DealTab = {
        dealId: 0,
        dealTitle: `Withdrawal ${dealTabs.length}`,
        segments: [],
        isFacility: false,
        isNew: true,
        isDirty: true
      };
      setDealTabs([...dealTabs, newTab]);
      setPrimaryTabIndex(dealTabs.length);
      setSecondaryTabIndex(0);
    }
  }, [currentPrimaryTab, dealTabs]);

  const handlePrimaryTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      if (currentPrimaryTab?.isDirty) {
        setPendingTabSwitch(newValue);
        setShowWarning(true);
      } else {
        setPrimaryTabIndex(newValue);
        // Set secondary tab index to the last segment of the new deal
        setSecondaryTabIndex(Math.max(0, dealTabs[newValue].segments.length - 1));
      }
    },
    [currentPrimaryTab, dealTabs]
  );

  const handleAddSecondaryTab = useCallback(
    (formValues: FormValues, segmentData: DebtSegment) => {
      const updatedTab = updateCurrentSegment(segmentData);
      const updatedSegments = [
        ...updatedTab.segments,
        {
          ...formValues,
          id: 0,
          deal_id: currentPrimaryTab?.dealId || 0,
          deal_title: currentPrimaryTab?.dealTitle || ""
        } as DebtSegment
      ];

      const updatedTabs = [...dealTabs];
      updatedTabs[primaryTabIndex] = {
        ...currentPrimaryTab!,
        segments: updatedSegments
      };
      setDealTabs(updatedTabs);
      setSecondaryTabIndex(updatedSegments.length - 1);
    },
    [currentSegments, currentPrimaryTab, dealTabs, primaryTabIndex]
  );

  const handleRemoveSecondaryTab = useCallback(
    (index: number) => {
      const updatedSegments = currentSegments.filter((_, i) => i !== index);
      const updatedTabs = [...dealTabs];
      updatedTabs[primaryTabIndex] = {
        ...currentPrimaryTab!,
        segments: updatedSegments
      };
      setDealTabs(updatedTabs);
      setSecondaryTabIndex(Math.max(0, index - 1));
    },
    [currentSegments, currentPrimaryTab, dealTabs, primaryTabIndex]
  );

  const handleConfirmTabSwitch = useCallback(
    (resetForm: () => void) => {
      const filteredTabs = dealTabs.filter((t) => !t.isNew).map((tab) => ({ ...tab, isDirty: false }));
      setDealTabs(filteredTabs);
      resetForm();

      if (pendingTabSwitch !== null) {
        setPrimaryTabIndex(pendingTabSwitch);
        setSecondaryTabIndex(0);
        setPendingTabSwitch(null);
      } else if (pendingAddWithdrawal) {
        const newTab: DealTab = {
          dealId: 0,
          dealTitle: `Withdrawal ${filteredTabs.length}`,
          segments: [],
          isFacility: false,
          isNew: true,
          isDirty: true
        };
        setDealTabs([...filteredTabs, newTab]);
        setPrimaryTabIndex(filteredTabs.length);
        setSecondaryTabIndex(0);
        setPendingAddWithdrawal(false);
      }
      setShowWarning(false);
    },
    [dealTabs, pendingTabSwitch, pendingAddWithdrawal]
  );

  const handleCancelTabSwitch = useCallback(() => {
    setPendingTabSwitch(null);
    setPendingAddWithdrawal(false);
    setShowWarning(false);
  }, []);

  const markTabDirty = useCallback(() => {
    setDealTabs((prev) => {
      const updated = [...prev];
      if (updated[primaryTabIndex]) {
        updated[primaryTabIndex] = { ...updated[primaryTabIndex], isDirty: true };
      }
      return updated;
    });
  }, [primaryTabIndex]);

  const clearTabDirty = useCallback(() => {
    setDealTabs((prev) => {
      const updated = [...prev];
      if (updated[primaryTabIndex]) {
        updated[primaryTabIndex] = { ...updated[primaryTabIndex], isDirty: false };
      }
      return updated;
    });
  }, [primaryTabIndex]);

  const updateCurrentSegment = useCallback(
    (segmentData: DebtSegment) => {
      const updatedTabs = [...dealTabs];
      const updatedSegments = [...currentSegments];
      updatedSegments[secondaryTabIndex] = segmentData;
      updatedTabs[primaryTabIndex] = {
        ...currentPrimaryTab!,
        segments: updatedSegments
      };
      setDealTabs(updatedTabs);
      return updatedTabs[primaryTabIndex];
    },
    [dealTabs, currentSegments, secondaryTabIndex, primaryTabIndex, currentPrimaryTab]
  );

  const resetTabs = useCallback(() => {
    setPrimaryTabIndex(0);
    setSecondaryTabIndex(0);
    setDealTabs([]);
  }, []);

  return {
    // State
    primaryTabIndex,
    secondaryTabIndex,
    dealTabs,
    showWarning,
    currentPrimaryTab,
    currentSegments,
    currentSegment,
    isFacilityTab,

    // Handlers
    handleAddWithdrawal,
    handlePrimaryTabChange,
    handleSecondaryTabChange: setSecondaryTabIndex,
    handleAddSecondaryTab,
    handleRemoveSecondaryTab,
    handleConfirmTabSwitch,
    handleCancelTabSwitch,
    markTabDirty,
    clearTabDirty,
    updateCurrentSegment,
    resetTabs
  };
}
